import React, { useState } from "react";
import { Card, Paper, Grid, Box } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PageLayout from "examples/LayoutContainers/PageLayout";
import AdminNavBar from "./AdminNavBar";
import eventManagementImage from "assets/images/event-management.jpg";
import chaptersImage from "assets/images/chapters.png";
import alumniRightsImage from "assets/images/alumni-rights.jpg";
import alumniDetailsImage from "assets/images/alumni-details.png";
import importStudentsImage from "assets/images/import-students.png";
import sendInvitationsImage from "assets/images/send-invitations.jpg";
import studentRequestsImage from "assets/images/student-requests.jpg";
import alumniBatchWiseImage from "assets/images/alumni-batch-wise.png";
import allAlumniImage from "assets/images/all-alumni.jpg";
import uploadCurrentStudentsImage from "assets/images/upload-current-students.jpg";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TableChartIcon from "@mui/icons-material/TableChart";
import SchoolIcon from "@mui/icons-material/School";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MailIcon from "@mui/icons-material/Mail";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import AdminChapterManager from "./AdminChapterManager";
import AdminEventManager from "./AdminEventManager";
import AdminAlumniRightsManager from "./AdminAlumniRightsManager";
import AdminAlumniDetailsDialog from "./AdminAlumniDetailsDialog";
import AdminImportStudentDetails from "./AdminImportStudentDetails";
import AdminAlumnusTableDialog from "./AdminAlumnusTableDialog";
import AdminAlumniBatchWiseDialog from "./AdminAlumniBatchWiseDialog";
import AdminUploadCurrentStudents from "./AdminUploadCurrentStudents";
import AdminSendStudentInvitations from "./AdminSendStudentInvitations";
import AdminStudentRequestsManager from "./AdminStudentRequestsManager";
import AdminImportExistingStudents from "./AdminImportExistingStudents";

// Standard card styles using MUI theme colors
const cardStyle = {
  p: 3,
  borderRadius: 2,
  textAlign: "center",
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  height: "100%",
  minHeight: 260,
  border: "1px solid #e0e0e0",
  cursor: "pointer",
  transition: "all 0.3s ease",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    borderColor: "primary.main",
  },
};

const getIconWrapperStyle = (color) => ({
  backgroundColor: (theme) => theme.palette[color]?.lighter || theme.palette.primary.lighter,
  padding: 2,
  borderRadius: "50%",
  mb: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 72,
  height: 72,
  transition: "all 0.3s ease",
});

const cardData = [
  {
    icon: <GroupWorkIcon sx={{ fontSize: 36, color: "primary.main" }} />,
    title: "Chapters",
    description: "Create, edit, and manage chapters for your alumni network.",
    button: "Manage Chapters",
    color: "primary",
    image: chaptersImage,
  },
  {
    icon: <EventIcon sx={{ fontSize: 36, color: "info.main" }} />,
    title: "Events",
    description: "Organize and oversee alumni events and gatherings.",
    button: "Manage Events",
    color: "info",
    image: eventManagementImage,
  },
  {
    icon: <VerifiedUserIcon sx={{ fontSize: 36, color: "success.main" }} />,
    title: "Alumni Rights",
    description: "Modify alumni access and rights with ease.",
    button: "Modify Rights",
    color: "success",
    image: alumniRightsImage,
  },
  {
    icon: <AccountCircleIcon sx={{ fontSize: 36, color: "warning.main" }} />,
    title: "Alumni Details",
    description: "View and manage detailed information about alumni.",
    button: "View Details",
    color: "warning",
    image: alumniDetailsImage,
  },
  {
    icon: <UploadFileIcon sx={{ fontSize: 36, color: "error.main" }} />,
    title: "Import Students",
    description: "Import student details from Excel spreadsheets.",
    button: "Import Data",
    color: "error",
    image: importStudentsImage,
  },
  {
    icon: <TableChartIcon sx={{ fontSize: 36, color: "primary.main" }} />,
    title: "All Alumni",
    description: "Browse all alumnus records in a table with search.",
    button: "View Table",
    color: "primary",
    image: allAlumniImage,
  },
  {
    icon: <SchoolIcon sx={{ fontSize: 36, color: "info.main" }} />,
    title: "Alumni Batch Wise",
    description: "View and manage alumni details organized by batch year.",
    button: "View Batches",
    color: "info",
    image: alumniBatchWiseImage,
  },
  {
    icon: <PersonAddIcon sx={{ fontSize: 36, color: "success.main" }} />,
    title: "Upload Current Students",
    description: "Upload current students and manage their portal access requests.",
    button: "Upload Students",
    color: "success",
    image: uploadCurrentStudentsImage,
  },
  {
    icon: <MailIcon sx={{ fontSize: 36, color: "warning.main" }} />,
    title: "Send Invitations",
    description: "Send invitations to current students to join the alumni portal.",
    button: "Send Invitations",
    color: "warning",
    image: sendInvitationsImage,
  },
  {
    icon: <AssignmentIcon sx={{ fontSize: 36, color: "error.main" }} />,
    title: "Student Requests",
    description: "View and approve/reject student requests for portal access.",
    button: "Manage Requests",
    color: "error",
    image: studentRequestsImage,
  },
  {
    icon: <UploadFileIcon sx={{ fontSize: 36, color: "primary.main" }} />,
    title: "Import Existing Students",
    description: "Import existing students and move them to the alumni portal.",
    button: "Import Students",
    color: "primary",
    image: uploadCurrentStudentsImage,
  },
];

const AdminHome = () => {
  const navigate = useNavigate();
  const [imageErrors, setImageErrors] = useState({});

  const [chapterDrawerOpen, setChapterDrawerOpen] = useState(false);
  const handleOpenChapterDrawer = () => setChapterDrawerOpen(true);
  const handleCloseChapterDrawer = () => setChapterDrawerOpen(false);
  const handleOpenChapterPage = () => navigate("/admin/chapters");

  const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
  const handleOpenEventDrawer = () => setEventDrawerOpen(true);
  const handleCloseEventDrawer = () => setEventDrawerOpen(false);

  const [alumniRightsDrawerOpen, setAlumniRightsDrawerOpen] = useState(false);
  const handleOpenAlumniRightsDrawer = () => setAlumniRightsDrawerOpen(true);
  const handleCloseAlumniRightsDrawer = () => setAlumniRightsDrawerOpen(false);

  const [alumniDetailsDialogOpen, setAlumniDetailsDialogOpen] = useState(false);
  const handleOpenAlumniDetailsDialog = () => setAlumniDetailsDialogOpen(true);
  const handleCloseAlumniDetailsDialog = () => setAlumniDetailsDialogOpen(false);

  const [importStudentDrawerOpen, setImportStudentDrawerOpen] = useState(false);
  const handleOpenImportStudentDrawer = () => setImportStudentDrawerOpen(true);
  const handleCloseImportStudentDrawer = () => setImportStudentDrawerOpen(false);

  // NEW: All Alumni table dialog
  const [alumnusTableDialogOpen, setAlumnusTableDialogOpen] = useState(false);
  const handleOpenAlumnusTableDialog = () => setAlumnusTableDialogOpen(true);
  const handleCloseAlumnusTableDialog = () => setAlumnusTableDialogOpen(false);

  // NEW: Alumni Batch Wise dialog
  const [alumniBatchDialogOpen, setAlumniBatchDialogOpen] = useState(false);
  const handleOpenAlumniBatchDialog = () => setAlumniBatchDialogOpen(true);
  const handleCloseAlumniBatchDialog = () => setAlumniBatchDialogOpen(false);

  // Current Students Management
  const [uploadCurrentStudentsOpen, setUploadCurrentStudentsOpen] = useState(false);
  const handleOpenUploadCurrentStudents = () => setUploadCurrentStudentsOpen(true);
  const handleCloseUploadCurrentStudents = () => setUploadCurrentStudentsOpen(false);

  const [sendInvitationsOpen, setSendInvitationsOpen] = useState(false);
  const handleOpenSendInvitations = () => setSendInvitationsOpen(true);
  const handleCloseSendInvitations = () => setSendInvitationsOpen(false);

  const [studentRequestsOpen, setStudentRequestsOpen] = useState(false);
  const handleOpenStudentRequests = () => setStudentRequestsOpen(true);
  const handleCloseStudentRequests = () => setStudentRequestsOpen(false);

  const [importExistingStudentsOpen, setImportExistingStudentsOpen] = useState(false);
  const handleOpenImportExistingStudents = () => setImportExistingStudentsOpen(true);
  const handleCloseImportExistingStudents = () => setImportExistingStudentsOpen(false);

  const isAnyDrawerOpen = chapterDrawerOpen || eventDrawerOpen || alumniRightsDrawerOpen;

  return (
    <PageLayout>
      {!isAnyDrawerOpen && <AdminNavBar />}
      <MDBox
        px={3}
        py={3}
        minHeight="calc(100vh - 120px)"
        sx={{
          background: "#f8f9fa",
          pt: { xs: 10, sm: 10, md: 4 },
        }}
      >
        {/* Hero Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: "white",
            mb: 3,
          }}
        >
          <MDTypography variant="h4" fontWeight="bold" color="primary" gutterBottom>
            Admin Dashboard
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Manage your alumni network, students, and portal access from one central location.
          </MDTypography>
        </Paper>

        {/* Statistics Cards */}
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  color: "#fff",
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <MDTypography variant="h6" fontWeight="bold" color="inherit">
                    Total Alumni
                  </MDTypography>
                  <PeopleIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                </Box>
                <MDTypography variant="h3" fontWeight="bold" color="inherit">
                  1,234
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.9 }}>
                  Active members
                </MDTypography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: "info.main",
                  color: "#fff",
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <MDTypography variant="h6" fontWeight="bold" color="inherit">
                    Active Events
                  </MDTypography>
                  <EventIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                </Box>
                <MDTypography variant="h3" fontWeight="bold" color="inherit">
                  12
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.9 }}>
                  Upcoming events
                </MDTypography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: "success.main",
                  color: "#fff",
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <MDTypography variant="h6" fontWeight="bold" color="inherit">
                    Students
                  </MDTypography>
                  <SchoolIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                </Box>
                <MDTypography variant="h3" fontWeight="bold" color="inherit">
                  567
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.9 }}>
                  Current students
                </MDTypography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: "warning.main",
                  color: "#fff",
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <MDTypography variant="h6" fontWeight="bold" color="inherit">
                    Growth
                  </MDTypography>
                  <TrendingUpIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                </Box>
                <MDTypography variant="h3" fontWeight="bold" color="inherit">
                  +24%
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.9 }}>
                  This month
                </MDTypography>
              </Paper>
            </Grid>
          </Grid>
        </MDBox>

        {/* Quick Actions Section */}
        <MDBox>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "white",
              mb: 3,
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <DashboardIcon sx={{ fontSize: 28, color: "primary.main", mr: 1.5 }} />
              <MDTypography variant="h5" fontWeight="bold" color="dark">
                Quick Actions
              </MDTypography>
            </Box>
            <MDTypography variant="body2" color="text.secondary">
              Access all administrative functions and manage your alumni network efficiently.
            </MDTypography>
          </Paper>

          <Grid container spacing={3}>
            {cardData.map((item, index) => (
              <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                <Card
                  sx={cardStyle}
                  onClick={
                    item.title === "Chapters"
                      ? handleOpenChapterDrawer
                      : item.title === "Events"
                      ? handleOpenEventDrawer
                      : item.title === "Alumni Rights"
                      ? handleOpenAlumniRightsDrawer
                      : item.title === "Alumni Details"
                      ? handleOpenAlumniDetailsDialog
                      : item.title === "Import Students"
                      ? handleOpenImportStudentDrawer
                      : item.title === "All Alumni"
                      ? handleOpenAlumnusTableDialog
                      : item.title === "Alumni Batch Wise"
                      ? handleOpenAlumniBatchDialog
                      : item.title === "Upload Current Students"
                      ? handleOpenUploadCurrentStudents
                      : item.title === "Send Invitations"
                      ? handleOpenSendInvitations
                      : item.title === "Student Requests"
                      ? handleOpenStudentRequests
                      : item.title === "Import Existing Students"
                      ? handleOpenImportExistingStudents
                      : undefined
                  }
                >
                  <Box>
                    {item.image && !imageErrors[item.title] ? (
                      <Box
                        sx={{
                          width: "100%",
                          height: 120,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          borderRadius: 2,
                          bgcolor: "grey.50",
                        }}
                      >
                        <img
                          src={item.image}
                          alt={item.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                          onError={() => setImageErrors((prev) => ({ ...prev, [item.title]: true }))}
                        />
                      </Box>
                    ) : (
                      <Box sx={getIconWrapperStyle(item.color)}>
                        {item.icon}
                      </Box>
                    )}
                    <MDTypography variant="h6" fontWeight="bold" color="dark" gutterBottom>
                      {item.title}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary" mb={2} sx={{ minHeight: 40 }}>
                      {item.description}
                    </MDTypography>
                  </Box>
                  <MDButton
                    variant="contained"
                    color={item.color}
                    endIcon={<ArrowForwardIosIcon sx={{ fontSize: 14 }} />}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                      py: 1,
                    }}
                  >
                    {item.button}
                  </MDButton>
                </Card>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      </MDBox>

      <Drawer
        anchor="right"
        open={chapterDrawerOpen}
        onClose={handleCloseChapterDrawer}
        PaperProps={{ sx: { width: 400 } }}
      >
        <AdminChapterManager onClose={handleCloseChapterDrawer} />
      </Drawer>

      <Drawer
        anchor="right"
        open={eventDrawerOpen}
        onClose={handleCloseEventDrawer}
        PaperProps={{ sx: { width: 400 } }}
      >
        <AdminEventManager onClose={handleCloseEventDrawer} />
      </Drawer>

      <Drawer
        anchor="right"
        open={alumniRightsDrawerOpen}
        onClose={handleCloseAlumniRightsDrawer}
        PaperProps={{ sx: { width: 400 } }}
      >
        <AdminAlumniRightsManager onClose={handleCloseAlumniRightsDrawer} />
      </Drawer>

      <AdminAlumniDetailsDialog
        open={alumniDetailsDialogOpen}
        onClose={handleCloseAlumniDetailsDialog}
      />

      <AdminImportStudentDetails
        open={importStudentDrawerOpen}
        onClose={handleCloseImportStudentDrawer}
      />

      {/* NEW: Full-screen table dialog */}
      <AdminAlumnusTableDialog
        open={alumnusTableDialogOpen}
        onClose={handleCloseAlumnusTableDialog}
      />

      {/* NEW: Alumni Batch Wise dialog */}
      <AdminAlumniBatchWiseDialog
        open={alumniBatchDialogOpen}
        onClose={handleCloseAlumniBatchDialog}
      />

      {/* Current Students Management */}
      <AdminUploadCurrentStudents
        open={uploadCurrentStudentsOpen}
        onClose={handleCloseUploadCurrentStudents}
      />

      <AdminSendStudentInvitations
        open={sendInvitationsOpen}
        onClose={handleCloseSendInvitations}
      />

      <AdminStudentRequestsManager
        open={studentRequestsOpen}
        onClose={handleCloseStudentRequests}
      />

      {/* Import Existing Students */}
      <AdminImportExistingStudents
        open={importExistingStudentsOpen}
        onClose={handleCloseImportExistingStudents}
      />
    </PageLayout>
  );
};

export default AdminHome;
